import { useEffect, useRef } from 'react';
import styled from 'styled-components';

import { UnderlinedTabs } from '@/ui/Tabs';
import { JourneyInsightsTabsParticipantsContent } from '@/features/journey/admin/organisms/JourneyInsightsTabsParticipantsContent';
import { JourneyInsightsTabsLearningItems } from '@/features/journey/admin/organisms/JourneyInsightsTabsLearningItems';
import { useQueryState, useQueryBooleanState } from '@/hooks/useQueryState';
import { SEARCH_PARAM_KEYS } from '@/features/management/manager/hooks/usePageState';

export enum QueryParams {
  Tab = 'tab',
  TabScroll = 'tabScroll',
}

enum Tab {
  LearningItems,
  Participants,
}

export enum TabQueryParam {
  LearningItems = 'learning-items',
  Participants = 'participants',
}

const TAB_QUERY_PARAM_TO_TAB: Record<TabQueryParam, Tab> = {
  [TabQueryParam.LearningItems]: Tab.LearningItems,
  [TabQueryParam.Participants]: Tab.Participants,
} as const;

const TabsWrapperStyled = styled.div`
  width: 100%;
`;

export const JourneyInsightsTabs = ({ spaceId }: { spaceId: number }): React.ReactElement => {
  const [activeQueryParamTab, setActiveQueryParamTab] = useQueryState(QueryParams.Tab, TabQueryParam.LearningItems);
  const [activeQueryParamTabScroll, setActiveQueryParamTabScroll] = useQueryBooleanState(QueryParams.TabScroll, false);
  const tabsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (activeQueryParamTabScroll) {
      tabsRef.current?.scrollIntoView?.();
      setActiveQueryParamTabScroll(undefined);
    }
  }, []);

  const navigationSetNextParams = (prevParams: URLSearchParams): URLSearchParams => {
    const nextParams = new URLSearchParams();

    if (prevParams.has(SEARCH_PARAM_KEYS.SELECTED_TEAM_IDS)) {
      nextParams.set(SEARCH_PARAM_KEYS.SELECTED_TEAM_IDS, prevParams.get(SEARCH_PARAM_KEYS.SELECTED_TEAM_IDS) || '');
    }

    return nextParams;
  };

  const handleTabChange = (tab: Tab) => {
    if (tab === Tab.Participants) {
      setActiveQueryParamTab(TabQueryParam.Participants, { setNextParams: navigationSetNextParams });

      return;
    }

    setActiveQueryParamTab(TabQueryParam.LearningItems, { setNextParams: navigationSetNextParams });
  };

  return (
    <TabsWrapperStyled ref={tabsRef}>
      <UnderlinedTabs
        activeTab={TAB_QUERY_PARAM_TO_TAB[activeQueryParamTab as TabQueryParam] || Tab.LearningItems}
        onActiveTabChange={handleTabChange}
      >
        <UnderlinedTabs.TriggersList>
          <UnderlinedTabs.Trigger label="Items" value={Tab.LearningItems} />
          <UnderlinedTabs.Trigger label="Learners" value={Tab.Participants} />
        </UnderlinedTabs.TriggersList>
        <UnderlinedTabs.ContentsList>
          <UnderlinedTabs.Content value={Tab.LearningItems}>
            <JourneyInsightsTabsLearningItems spaceId={spaceId} />
          </UnderlinedTabs.Content>
          <UnderlinedTabs.Content value={Tab.Participants}>
            <JourneyInsightsTabsParticipantsContent spaceId={spaceId} />
          </UnderlinedTabs.Content>
        </UnderlinedTabs.ContentsList>
      </UnderlinedTabs>
    </TabsWrapperStyled>
  );
};
